import {
  type PersonBaseBasicInformation,
  type MemberStaffInformation,
  type PersonUrl,
  type Role,
  type LegRole,
  type LegSeat,
  type CommitteeStaffInformation,
  type LeadershipPhone,
  type UsPhone,
  type CommitteePhone,
  type LeadershipStaffInformation,
  type CommitteeAddress,
  type CommitteeBasicInformation,
  type CommitteeBaseInformation,
  type CommitteeUrl, type LeadershipOfficeJurisdiction, type CommitteeJurisdiction,
  type Building,
  type MemberInformation,
  type ExecOrgParentLutItem,
  type UsAddress, type LeadershipAddress,
  type Prefix
} from 'services'
import { type Chamber, type State, type PersonAddress, type PersonPhone } from 'components'
import {
  suffixOptions,
  PHONE_TYPES,
  STATE_CHAMBERS,
  HOUSE_CHAMBERS,
  JOINT_CHAMBERS,
  FEDERAL_EXECUTIVE_ROLES
} from 'utils'
export function generateLegalName (person: PersonBaseBasicInformation, prefixes: Prefix[]): string {
  const { firstName, middleName, lastName, suffixId, prefixId } = person

  let legalName = ''

  if ((firstName != null) && (lastName != null)) {
    legalName = `${firstName.trim()} ${(middleName != null && middleName.length > 0) ? middleName.trim() + ' ' : ''}${lastName.trim()}`
  }

  if (suffixId != null && suffixId > 0) {
    const suffixText = suffixOptions.find(option => option.id === suffixId)?.value

    if (suffixText != null) {
      legalName += ` ${suffixText}`
    }
  }

  if (prefixId != null && prefixId > 0) {
    const prefixText = prefixes.find(prefix => prefix.id === prefixId)?.code

    if (prefixText != null) {
      legalName = `${prefixText} ${legalName}`
    }
  }

  return legalName
}

export function generateStafferEmail (staffer: MemberStaffInformation | CommitteeStaffInformation | LeadershipStaffInformation): string {
  const { firstName, lastName } = staffer?.person
  const chamber = 'committee' in staffer
    ? staffer.committee?.chamber?.desc
    : 'legmem' in staffer
      ? staffer?.legmem?.legseat?.legdistrict?.legchamber?.desc
      : staffer.leadershipOfficer.ofcname

  const legMemLastName: string | null = ('committee' in staffer || 'leadershipOfficer' in staffer)
    ? null
    : staffer?.legmem?.person?.lastName

  const houseChambers = ['US House', 'State House']
  const senateChambers = ['US Senate', 'State Senate']

  if (firstName == null || lastName == null || chamber == null) {
    return ''
  }

  let domain = ''
  let separator = ''

  if (houseChambers.some(cham => chamber.includes(cham))) {
    domain = 'mail.house.gov'
    separator = '.'
  } else if ((legMemLastName != null) && senateChambers.some(cham => chamber.includes(cham))) {
    domain = `${legMemLastName.trim().toLowerCase()}.senate.gov`
    separator = '_'
  }

  return `${firstName.trim().toLowerCase()}${separator}${lastName.trim().toLowerCase()}@${domain}`
}

export const getEmptyStafferEmail = (personId: number): PersonUrl => {
  return {
    id: 0,
    value: '',
    personId: personId ?? 0,
    lastUpdated: new Date().toISOString(),
    typeId: 1,
    descId: 8,
    displayOrder: 1
  }
}

export const getEmptyRole = (): Role => {
  return {
    id: 0,
    desc: '',
    code: '',
    branch: {
      id: 0,
      branch_desc: ''
    }
  }
}

export const getEmptyLegRole = (): LegRole => {
  return {
    id: 0,
    descShort: '',
    descLong: '',
    code: '',
    lutOrder: 0,
    updated_by: '',
    last_updated: '',
    role: getEmptyRole()
  }
}

export const getEmptyLegSeat = (): LegSeat => {
  return {
    id: 0,
    legdistrict: {
      id: 0,
      state: {
        id: 0,
        desc: '',
        abbrev: ''
      },
      legchamber: {
        id: 0,
        code: '',
        desc: ''
      },
      code: '',
      desc: '',
      type: '',
      members: 0,
      counties: 0,
      mapLink: ''
    },
    status: {
      id: 0,
      statusCode: '',
      statusDesc: '',
      lutOrder: 0
    },
    seatTrailer: ''
  }
}

export const getEmptyPersonAddress = (personId: number): PersonAddress => {
  return {
    id: 0,
    personId,
    primary: {
      building: '',
      room: '',
      addressOne: '',
      addressTwo: '',
      city: '',
      state: { id: 0 },
      country: { id: 223, country_desc: '', country_code: '', states: [] },
      zipCode: ''
    },
    secondary: {
      building: '',
      room: '',
      addressOne: '',
      addressTwo: '',
      city: '',
      state: { id: 0 },
      country: { id: 223, country_desc: '', country_code: '', states: [] },
      zipCode: ''
    },
    typeId: 6,
    mailingAddress: 1,
    phones: [],
    displayOrder: 1,
    updatedBy: undefined,
    lastUpdated: undefined
  }
}

const createBaseEmptyPhone = (addressId: number): UsPhone => {
  return {
    id: -Math.floor(Math.random() * 900000 + 100000), // Random negative 6-digit number
    addressId,
    areaCode: '',
    countryCode: '+1',
    phoneNumber: '',
    displayOrder: 0,
    extension: '',
    typeId: PHONE_TYPES.PHONE
  }
}

export const getEmptyPersonPhone = (personId: number, addressId: number): PersonPhone => {
  return {
    ...createBaseEmptyPhone(addressId),
    personId
  }
}

export const createFilledPersonPhone = (personId: number, addressId: number, phoneData: UsPhone): PersonPhone => {
  const basePhone = getEmptyPersonPhone(personId, addressId)
  basePhone.countryCode = phoneData.countryCode
  basePhone.areaCode = phoneData.areaCode
  basePhone.phoneNumber = phoneData.phoneNumber
  basePhone.extension = phoneData.extension
  basePhone.typeId = phoneData.typeId

  return basePhone
}

export const buildStafferAddressFromSource = (
  targetAddress: PersonAddress,
  source: { primary: UsAddress, secondary: UsAddress, mailingAddress: 1 | 2, phones: UsPhone[] },
  personId: number,
  phoneTypeIds?: number[]
): PersonAddress => {
  const sourcePhones = phoneTypeIds != null
    ? source.phones.filter(phone => phoneTypeIds.includes(phone.typeId))
    : source.phones
  return {
    ...targetAddress,
    primary: { ...source.primary },
    secondary: { ...source.secondary },
    mailingAddress: source.mailingAddress,
    phones: sourcePhones.map(phone => createFilledPersonPhone(personId, targetAddress.id, phone)),
    personId,
    typeId: targetAddress.typeId
  }
}

export const getEmptyLeadershipPhone = (leadershipOfficerId: number, addressId: number): LeadershipPhone => {
  return {
    ...createBaseEmptyPhone(addressId),
    legOfcId: leadershipOfficerId
  }
}

export const getEmptyCommitteePhone = (committeeId: number, addressId: number, typeId: number): CommitteePhone => {
  return {
    ...createBaseEmptyPhone(addressId),
    committeeId,
    typeId,
    displayOrder: 1
  }
}

export const getEmptyPersonUrl = (personId: number, displayOrder: number): PersonUrl => {
  return {
    id: -1,
    typeId: 1,
    value: '',
    personId,
    displayOrder,
    lastUpdated: new Date().toISOString(),
    descId: 1
  }
}

export const getEmptyPersonBasicInfo = (): PersonBaseBasicInformation => {
  return {
    id: 0,
    lastName: '',
    firstName: '',
    middleName: '',
    prefixId: 0,
    lastUpdated: ''
  }
}

export const getEmptyCommitteeAddress = (committeeId: number, displayOrder: number = 2): CommitteeAddress => {
  return {
    id: 0,
    committeeId,
    primary: {
      building: '',
      room: '',
      addressOne: '',
      addressTwo: '',
      city: '',
      state: { id: 0 },
      country: { id: 223, country_desc: '', country_code: '', states: [] },
      zipCode: ''
    },
    secondary: {
      building: '',
      room: '',
      addressOne: '',
      addressTwo: '',
      city: '',
      state: { id: 0 },
      country: { id: 223, country_desc: '', country_code: '', states: [] },
      zipCode: ''
    },
    typeId: 1,
    mailingAddress: 1,
    phones: [],
    displayOrder,
    updatedBy: undefined,
    lastUpdated: undefined
  }
}

// Define a type for committee creation that allows nullable fields
export type CommitteeCreationInfo = Omit<CommitteeBasicInformation, 'chamber' | 'role' | 'state' | 'parent' | 'lastUpdated' | 'updatedBy'> & {
  chamber: Chamber | null
  role: Role | null
  state: State | null
  parent: CommitteeBaseInformation | null
}

export const getEmptyCommitteeBasicInfo = (states: Map<number, State>): CommitteeCreationInfo => {
  return {
    id: 0,
    name: '',
    displayName: '',
    code: '',
    chamber: null,
    active: true,
    role: null,
    state: states.get(0) ?? null,
    parent: null,
    addresses: [],
    urls: [],
    responsibilities: []
  }
}

export const getEmptyBuildingAddress = (): Building => ({
  id: 0,
  displayName: '',
  buildingName: null,
  room: null,
  street: null,
  street2: null,
  city: null,
  state: '',
  country: null,
  zipCode: '',
  legType: '',
  chamber: null,
  updatedBy: null,
  lastUpdated: null
})

export const getEmptyCommitteeUrl = (committeeId: number, displayOrder: number, typeId: number, descId: number): CommitteeUrl => {
  return {
    id: -1,
    typeId,
    descId,
    value: '',
    committeeId,
    displayOrder, // 1 for Majority, 2 for Minority
    lastUpdated: new Date().toISOString()
  }
}

export function formatFullName (person: PersonBaseBasicInformation, prefixes: Prefix[]): string {
  const prefix = person.prefixId != null && person.prefixId > 0
    ? prefixes.find(option => option.id === person.prefixId)?.code ?? ''
    : ''
  const suffix = person.suffixId != null && person.suffixId > 0
    ? suffixOptions.find(option => option.id === person.suffixId)?.value ?? ''
    : ''
  const firstName = person.firstName ?? ''
  const middleName = person.middleName ?? ''
  const nickname = person.nickname ?? ''

  const showNickname = nickname !== '' && nickname !== firstName && nickname !== middleName
  const givenNames = [prefix, firstName, middleName, showNickname ? `'${nickname}'` : '']
    .filter(part => part !== '')
    .join(' ')
  const familyName = [person.lastName, suffix].filter(part => part !== '').join(' ')

  if (givenNames === '') return familyName
  if (familyName === '') return givenNames
  return `${familyName}, ${givenNames}`
}

// Define a type for leadership officer creation that allows nullable fields
export interface LeadershipOfficerCreationInfo {
  id: number
  ofcname: string
  legchamber: Chamber | null
  role: Role | null
  state: State | null
  address: LeadershipAddress
}

export const getBasicLeadershipOfficerInfo = (states: Map<number, State>): LeadershipOfficerCreationInfo => {
  return {
    id: 0,
    ofcname: '',
    legchamber: null,
    role: null,
    state: states.get(0) ?? null,
    address: getEmptyLeadershipAddress(0)
  }
}

export const getEmptyLeadershipAddress = (leadershipOfficerId: number, displayOrder: number = 1): any => {
  return {
    id: 0,
    legOfcId: leadershipOfficerId,
    primary: {
      building: '',
      room: '',
      addressOne: '',
      addressTwo: '',
      city: '',
      state: { id: 0 },
      country: { id: 223, country_desc: '', country_code: '', states: [] },
      zipCode: ''
    },
    secondary: {
      building: '',
      room: '',
      addressOne: '',
      addressTwo: '',
      city: '',
      state: { id: 0 },
      country: { id: 223, country_desc: '', country_code: '', states: [] },
      zipCode: ''
    },
    typeId: 1,
    mailingAddress: 1,
    phones: [],
    displayOrder,
    updatedBy: undefined,
    lastUpdated: undefined
  }
}

export const getEmptyLeadershipUrl = (leadershipOfficerId: number, displayOrder: number, isWebsite: boolean): any => {
  return {
    id: -1,
    typeId: isWebsite ? 2 : 1, // 1 for Email, 2 for Website
    descId: isWebsite ? 3 : 1, // Using URL_DESC_TYPES values
    value: '',
    legOfcId: leadershipOfficerId,
    displayOrder,
    lastUpdated: new Date().toISOString()
  }
}

export const createEmptyLeadershipJurisdiction = (leadershipOfficerId: number): LeadershipOfficeJurisdiction => {
  return {
    id: 0,
    legOfcId: leadershipOfficerId,
    document: '',
    documentSource: '',
    documentDate: null,
    displayOrder: 1,
    lastUpdated: null,
    updatedBy: null
  }
}

export const getEmptyBaseBackgroundInfo = (personId: number, displayOrder: number = 1): any => {
  return {
    id: 0,
    personId,
    displayOrder,
    updatedBy: null,
    lastUpdated: null
  }
}

export const getEmptyKnowWhoBio = (personId: number, displayOrder: number = 1): any => {
  return {
    ...getEmptyBaseBackgroundInfo(personId, displayOrder),
    bioType: 'Person Bio',
    bioText: '',
    htmlReady: false,
    createdBy: null,
    lastCreated: null
  }
}

export const getEmptyNarrativeBio = (personId: number, displayOrder: number = 1): any => {
  return {
    ...getEmptyBaseBackgroundInfo(personId, displayOrder),
    bioText: '',
    bioSource: '',
    bioDate: ''
  }
}

export const getEmptyAward = (personId: number, displayOrder: number = 1): any => {
  return {
    ...getEmptyBaseBackgroundInfo(personId, displayOrder),
    typeId: 1,
    organization: '',
    awardTitle: '',
    awardYear: '',
    details: ''
  }
}

export const getEmptyDirector = (personId: number, displayOrder: number = 1): any => {
  return {
    ...getEmptyBaseBackgroundInfo(personId, displayOrder),
    typeId: 1,
    company: '',
    title: '',
    startYear: '',
    endYear: '',
    details: ''
  }
}

export const getEmptyEducation = (personId: number, displayOrder: number = 1): any => {
  return {
    ...getEmptyBaseBackgroundInfo(personId, displayOrder),
    degreeId: 0,
    majors: '',
    minors: '',
    schoolName: '',
    startDate: '',
    endDate: '',
    details: ''
  }
}

export const getEmptyHobbyInterest = (personId: number, displayOrder: number = 1): any => {
  return {
    ...getEmptyBaseBackgroundInfo(personId, displayOrder),
    interest: ''
  }
}

export const getEmptyMilitaryService = (personId: number, displayOrder: number = 1): any => {
  return {
    ...getEmptyBaseBackgroundInfo(personId, displayOrder),
    branch: '',
    startRank: '',
    highestRank: '',
    medals: '',
    details: ''
  }
}

export const getEmptyNotableFact = (personId: number, displayOrder: number = 1): any => {
  return {
    ...getEmptyBaseBackgroundInfo(personId, displayOrder),
    factText: '',
    factSource: '',
    factDate: ''
  }
}

export const getEmptyOrgMembership = (personId: number, displayOrder: number = 1): any => {
  return {
    ...getEmptyBaseBackgroundInfo(personId, displayOrder),
    typeId: 1,
    organization: '',
    title: '',
    startYear: '',
    endYear: '',
    details: ''
  }
}

export const getEmptyPoliticalExperience = (personId: number, displayOrder: number = 1): any => {
  return {
    ...getEmptyBaseBackgroundInfo(personId, displayOrder),
    organization: '',
    title: '',
    experienceDesc: '',
    startDate: '',
    endDate: ''
  }
}

export const getEmptyPreviousOccupation = (personId: number, displayOrder: number = 1): any => {
  return {
    ...getEmptyBaseBackgroundInfo(personId, displayOrder),
    occCode: null,
    occupation: '',
    company: '',
    startDate: '',
    endDate: '',
    details: ''
  }
}

export const getEmptyPublicationCredit = (personId: number, displayOrder: number = 1): any => {
  return {
    ...getEmptyBaseBackgroundInfo(personId, displayOrder),
    typeId: 1,
    pubByIn: '',
    pubTitle: '',
    pubYear: '',
    details: ''
  }
}

export const createEmptyCommitteeJurisdiction = (committeeId: number): CommitteeJurisdiction => {
  return {
    id: 0,
    committeeId,
    document: '',
    documentSource: '',
    documentDate: null,
    displayOrder: 1,
    lastUpdated: null,
    updatedBy: null
  }
}

export const BUILDING_LEG_TYPES = {
  STATE_LEGISLATOR: 'SL',
  FEDERAL_LEGISLATOR: 'FL',
  STATE_COMMITTEE: 'SC',
  FEDERAL_COMMITTEE: 'FC',
  FEDERAL_OFFICE: 'FO',
  STATE_OFFICE: 'SO',
  FEDERAL_EXECUTIVE: 'FE',
  STATE_EXECUTIVE: 'SE'
}

export const BUILDING_CHAMBERS = {
  SENATE: 'S',
  HOUSE: 'H',
  JOINT: 'J'
}

export const BUILDING_LEG_TYPE_OPTIONS = [
  { value: BUILDING_LEG_TYPES.FEDERAL_LEGISLATOR, label: `${BUILDING_LEG_TYPES.FEDERAL_LEGISLATOR} - Federal Legislative` },
  { value: BUILDING_LEG_TYPES.FEDERAL_COMMITTEE, label: `${BUILDING_LEG_TYPES.FEDERAL_COMMITTEE} - Federal Committee` },
  { value: BUILDING_LEG_TYPES.FEDERAL_OFFICE, label: `${BUILDING_LEG_TYPES.FEDERAL_OFFICE} - Federal Office` },
  { value: BUILDING_LEG_TYPES.FEDERAL_EXECUTIVE, label: `${BUILDING_LEG_TYPES.FEDERAL_EXECUTIVE} - Federal Executive` },
  { value: BUILDING_LEG_TYPES.STATE_LEGISLATOR, label: `${BUILDING_LEG_TYPES.STATE_LEGISLATOR} - State Legislative` },
  { value: BUILDING_LEG_TYPES.STATE_COMMITTEE, label: `${BUILDING_LEG_TYPES.STATE_COMMITTEE} - State Committee` },
  { value: BUILDING_LEG_TYPES.STATE_OFFICE, label: `${BUILDING_LEG_TYPES.STATE_OFFICE} - State Office` },
  { value: BUILDING_LEG_TYPES.STATE_EXECUTIVE, label: `${BUILDING_LEG_TYPES.STATE_EXECUTIVE} - State Executive` }
]

export const BUILDING_CHAMBER_OPTIONS = [
  { value: '', label: 'None' },
  { value: BUILDING_CHAMBERS.HOUSE, label: `${BUILDING_CHAMBERS.HOUSE} - House` },
  { value: BUILDING_CHAMBERS.SENATE, label: `${BUILDING_CHAMBERS.SENATE} - Senate` },
  { value: BUILDING_CHAMBERS.JOINT, label: `${BUILDING_CHAMBERS.JOINT} - Joint` }
]

export type BuildingChamberMode = 'legislative' | 'federalExec' | 'none'

export const buildingChamberMode = (legType: string | null | undefined): BuildingChamberMode => {
  if (legType === BUILDING_LEG_TYPES.FEDERAL_EXECUTIVE) return 'federalExec'
  if (legType === BUILDING_LEG_TYPES.STATE_EXECUTIVE) return 'none'
  return 'legislative'
}

export const SeatStatusConstants = {
  VAC_PREFIX: 'VAC',
  VACANT_PERSON_LASTNAME: 'Vacant'
} as const

export const isVacantStatus = (statusCode: string | null | undefined): boolean => {
  return statusCode?.toUpperCase().startsWith(SeatStatusConstants.VAC_PREFIX) ?? false
}

export const isVacantPerson = (position: MemberInformation): boolean => {
  const person = position.person

  if (person === null || person === undefined) return true

  if (person.lastName === SeatStatusConstants.VACANT_PERSON_LASTNAME) return true

  if (person.id !== null && person.id !== undefined && person.id > 0) return false

  const hasValidName = (person.firstName?.trim() ?? '') !== '' || (person.lastName?.trim() ?? '') !== ''

  return !hasValidName
}

// Exec-position vacancy predicate — mirrors the backend
// `SeatStatusHelper.is_person_vacant`: lastName='Vacant' AND empty firstName.
// Use this anywhere FE conditional UI must stay in lockstep with the BE's
// delete-vacant-position gate (search row, org detail, position update).
// Kept separate from `isVacantPerson` above, which is typed for legislators
// and applies a looser predicate (any blank-name person also counts).
export const isExecPositionRowVacant = (
  person: { firstName?: string | null, lastName?: string | null } | null | undefined
): boolean => {
  if (person == null) return false
  if (person.lastName !== SeatStatusConstants.VACANT_PERSON_LASTNAME) return false
  const firstName = person.firstName
  return firstName == null || firstName.trim() === ''
}

export const executivePositionsSearchPath = (
  orgId: number, topOrgId: number | null
): string => {
  const parentOrgId = topOrgId ?? orgId
  const base = `/us/executives/search?parentOrg=${parentOrgId}`
  return topOrgId != null && topOrgId !== orgId ? `${base}&childOrg=${orgId}` : base
}

export type EditableVacantField =
  | 'seatStatus'
  | 'electDate'
  | 'termNumber'
  | 'electCode'
  | 'percentVote'
  | 'reelectYear'
  | 'fecLinkNum'
  | 'majmin'
  | 'pyracusc'
  | 'cyracusc'
  | 'pyradasc'
  | 'cyradasc'
  | 'pyraflsc'
  | 'cyraflsc'
  | 'pyruscosc'
  | 'cyruscosc'

export const EDITABLE_FIELDS_FOR_VACANT = new Set<string>([
  'seatStatus',
  'electDate',
  'termNumber',
  'electCode',
  'percentVote',
  'reelectYear',
  'fecLinkNum',
  'majmin',
  'pyracusc',
  'cyracusc',
  'pyradasc',
  'cyradasc',
  'pyraflsc',
  'cyraflsc',
  'pyruscosc',
  'cyruscosc'
])

export const isFieldEditableForVacant = (fieldName: string): boolean => {
  return EDITABLE_FIELDS_FOR_VACANT.has(fieldName)
}

export const filterBuildingsForMemberByChamber = (
  buildings: Building[],
  chamberId: number | undefined,
  stateAbbrev: string | undefined
): Building[] => {
  if (chamberId == null) return []

  const legType = STATE_CHAMBERS.some(chamber => chamber.id === chamberId) ? BUILDING_LEG_TYPES.STATE_LEGISLATOR : BUILDING_LEG_TYPES.FEDERAL_LEGISLATOR
  const chamber = HOUSE_CHAMBERS.some(chamber => chamber.id === chamberId) ? BUILDING_CHAMBERS.HOUSE : BUILDING_CHAMBERS.SENATE

  return buildings.filter(b =>
    b.legType === legType &&
      b.chamber === chamber &&
      (legType !== BUILDING_LEG_TYPES.STATE_LEGISLATOR || b.state === stateAbbrev)
  )
}

export const filterBuildingsForMember = (buildings: Building[], member: MemberInformation): Building[] => {
  return filterBuildingsForMemberByChamber(
    buildings,
    member.legseat?.legdistrict?.legchamber?.id,
    member.legseat?.legdistrict?.state?.abbrev
  )
}

export const filterBuildingsForCommittee = (buildings: Building[], committee: CommitteeBasicInformation): Building[] => {
  const chamberId = committee.chamber?.id
  const stateAbbrev = committee.state?.abbrev

  if (chamberId == null) return []

  const legType = STATE_CHAMBERS.some(c => c.id === chamberId) ? BUILDING_LEG_TYPES.STATE_COMMITTEE : BUILDING_LEG_TYPES.FEDERAL_COMMITTEE
  const chamber = JOINT_CHAMBERS.some(c => c.id === chamberId)
    ? BUILDING_CHAMBERS.JOINT
    : HOUSE_CHAMBERS.some(c => c.id === chamberId)
      ? BUILDING_CHAMBERS.HOUSE
      : BUILDING_CHAMBERS.SENATE

  return buildings.filter(b =>
    b.legType === legType &&
    b.chamber === chamber &&
    (legType !== BUILDING_LEG_TYPES.STATE_COMMITTEE || b.state === stateAbbrev)
  )
}

export const filterBuildingsForLeadership = (buildings: Building[], legOfc: { legchamber?: Chamber | null, state?: State | null }): Building[] => {
  const iOfcChamber = legOfc.legchamber?.id
  const stateAbbrev = legOfc.state?.abbrev

  if (iOfcChamber == null) return []

  const legType = STATE_CHAMBERS.some(chamber => chamber.id === iOfcChamber) ? BUILDING_LEG_TYPES.STATE_OFFICE : BUILDING_LEG_TYPES.FEDERAL_OFFICE
  const chamber = HOUSE_CHAMBERS.some(chamber => chamber.id === iOfcChamber) ? BUILDING_CHAMBERS.HOUSE : BUILDING_CHAMBERS.SENATE

  return buildings.filter(b =>
    b.legType === legType &&
      b.chamber === chamber &&
      (legType !== BUILDING_LEG_TYPES.STATE_OFFICE || b.state === stateAbbrev)
  )
}

export const EXEC_BUILDING_CHAMBER_PAD = 5

export const execBuildingChamber = (orgId: number): string =>
  String(orgId).padStart(EXEC_BUILDING_CHAMBER_PAD, '0')

export const STATE_EXEC_BUILDING_CHAMBER = '00000'

const FED_EXEC_ROLE_IDS = new Set<number>(FEDERAL_EXECUTIVE_ROLES)

export const federalExecChamberOptions = (
  parents: Map<number, ExecOrgParentLutItem>,
  current?: string | null
): Array<{ value: string, label: string }> => {
  const options = Array.from(parents.values())
    .filter(parent => FED_EXEC_ROLE_IDS.has(parent.role.id))
    .map(parent => ({
      value: execBuildingChamber(parent.id),
      label: `${execBuildingChamber(parent.id)} - ${parent.name}`
    }))
  return current != null && current !== '' && !options.some(o => o.value === current)
    ? [...options, { value: current, label: current }]
    : options
}

export const filterBuildingsForExecutive = (
  buildings: Building[],
  isStateExec: boolean,
  topOrgId: number | null | undefined,
  stateAbbrev: string | null | undefined
): Building[] => {
  if (isStateExec) {
    if (stateAbbrev == null || stateAbbrev === '') return []
    return buildings.filter(b =>
      b.legType === BUILDING_LEG_TYPES.STATE_EXECUTIVE && b.state === stateAbbrev
    )
  }

  if (topOrgId == null) return []
  const chamber = execBuildingChamber(topOrgId)
  return buildings.filter(b =>
    b.legType === BUILDING_LEG_TYPES.FEDERAL_EXECUTIVE && b.chamber === chamber
  )
}

export const applyBuildingInfo = (buildingInfo: Building, prevAddress: UsAddress, states: Map<number, State>): UsAddress => {
  const stateObject = buildingInfo.state != null
    ? Array.from(states.values()).find(state => state.abbrev === buildingInfo.state) ?? null
    : null
  return {
    ...prevAddress,
    building: buildingInfo.buildingName ?? '',
    room: buildingInfo.room ?? '',
    addressOne: buildingInfo.street ?? '',
    addressTwo: buildingInfo.street2 ?? '',
    city: buildingInfo.city ?? '',
    state: stateObject,
    country: { id: 223, country_desc: 'United States', country_code: '', states: [] },
    zipCode: buildingInfo.zipCode ?? ''
  }
}
